import { useContext } from 'react';
import styled, { css } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { Deactivation } from '@elfsight/widget-deactivation';
import { FreeLink, pickFreeLinkSettings } from '@elfsight/widget-free-link';
import { useBreakpoints, useRerender } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { useSettings } from '@wu/widget-core';
import classNames from 'classnames';
import { useRect } from 'react-use-rect';
import { meta } from '../../meta';
import { VerificationContext } from '../providers';
import { ItemAllow } from './ItemAllow';
import { ItemImage } from './ItemImage';

const MOBILE_BREAKPOINT = 600;

export const Item = () => {
  const { allow } = useContext(VerificationContext);
  const [breakpoints, container] = useBreakpoints(MOBILE_BREAKPOINT);
  const settings = useSettings();
  const freeLinkSettings = pickFreeLinkSettings(settings);

  const {
    message,
    caption,
    additionalInfo,
    textColor,
    messageFont,
    logo,
    textFont,
    image,
    underAgeMessage,
    popupBackgroundColor,
    shape,
    deactivate,
    additionalTextFont,
    align,
    underAgeAction
  } = settings;

  const rerender = useRerender();
  const [rectRef] = useRect(rerender, {
    resize: true
  });
  const mergedRef = useMergedRef(container, rectRef);

  return (
    <Container
      ref={mergedRef}
      _isMobile={breakpoints[MOBILE_BREAKPOINT]}
      _shape={shape}
    >
      <Show when={underAgeAction === 'message'}>
        <Error _show={allow === false}>
          <div dangerouslySetInnerHTML={{ __html: underAgeMessage }} />
        </Error>
      </Show>

      <Content
        _hasImage={!!image}
        _isMobile={breakpoints[MOBILE_BREAKPOINT]}
        _popupBackgroundColor={popupBackgroundColor}
      >
        <Show when={!!logo?.url}>
          <LogoContainer>
            <Logo src={logo?.url} alt="Content Logo" />
          </LogoContainer>
        </Show>

        <Message
          dangerouslySetInnerHTML={{ __html: message }}
          _textColor={textColor}
          _fontSize={messageFont.fontSize || 30}
          _fontStyle={messageFont.fontStyle || 'none'}
          _fontWeight={messageFont.fontWeight || 700}
          _align={align}
        />

        <Caption
          dangerouslySetInnerHTML={{ __html: caption }}
          _textColor={textColor}
          _fontSize={textFont.fontSize}
          _fontStyle={textFont.fontStyle}
          _fontWeight={textFont.fontWeight}
          _align={align}
        />

        <ItemAllow />

        <AdditionalInfo
          dangerouslySetInnerHTML={{ __html: additionalInfo }}
          _textColor={textColor}
          _fontSize={additionalTextFont.fontSize || 10}
          _fontStyle={additionalTextFont.fontStyle || 'none'}
          _fontWeight={additionalTextFont.fontWeight || 400}
          _align={align}
        />

        <FreeLinkContainer>
          <FreeLink
            {...freeLinkSettings}
            appAlias={meta.alias}
            appName={meta.name}
            style={{
              link: {
                marginTop: '20px'
              }
            }}
          />
        </FreeLinkContainer>
      </Content>

      <Show when={!!image}>
        <Image _isMobile={breakpoints[MOBILE_BREAKPOINT]}>
          <ItemImage />
        </Image>
      </Show>

      <Deactivation appAlias={meta.alias} displayDeactivation={deactivate} />
    </Container>
  );
};

const Container = styled.div.attrs(({ _isMobile }) => ({
  className: classNames(`${meta.prefix}-item-container`, {
    [`${meta.prefix}-item-container-mobile`]: _isMobile
  })
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: ${({ _shape }) => (_shape ? `${_shape}px` : '0')};
`;

const FreeLinkContainer = styled.div``;

const Content = styled.div.attrs({
  className: `${meta.prefix}-item-content`
})`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  padding: 40px 20px;
  width: ${({ _hasImage, _isMobile }) =>
    !_hasImage || _isMobile ? '100%' : '50%'};
  box-sizing: border-box;
  height: 100%;
  background-color: ${({ _popupBackgroundColor }) =>
    _popupBackgroundColor || 'transparent'};
  max-width: ${({ _hasImage, _isMobile }) =>
    !_hasImage || _isMobile ? '460px' : 'none'};
`;

const LogoContainer = styled.div.attrs({
  className: `${meta.prefix}-item-logoContainer`
})`
  overflow: hidden;
  max-width: 120px;
  max-height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  flex-shrink: 0;
`;

const Logo = styled.img.attrs({
  className: `${meta.prefix}-item-logo`
})`
  max-height: 100%;
  max-width: 100%;
  margin: 0 auto;
  flex-shrink: 0;
  object-fit: contain;
`;

const textStyles = css`
  width: 100%;

  ${({ _align, _fontWeight, _fontSize, _fontStyle, _textColor }) => css`
    text-align: ${_align};
    font-weight: ${_fontWeight};
    font-size: ${_fontSize}px;
    font-style: ${_fontStyle};
    color: ${_textColor};

    & a {
      color: ${_textColor};
    }
  `}
`;

const Message = styled.div.attrs({
  className: `${meta.prefix}-item-message`
})`
  margin-top: 32px;
  line-height: 1.15;
  white-space: pre-wrap;
  ${textStyles}
`;

const Caption = styled.div.attrs({
  className: `${meta.prefix}-item-caption`
})`
  margin-top: 16px;
  line-height: 1.25;
  ${textStyles}
`;

const AdditionalInfo = styled.div.attrs({
  className: `${meta.prefix}-item-additionalInfo`
})`
  display: block;
  margin-top: 20px;
  line-height: 1.25;
  ${textStyles}
`;

const Image = styled.div.attrs({
  className: `${meta.prefix}-item-image`
})`
  display: ${({ _isMobile }) => (_isMobile ? 'none' : 'block')};
  width: 50%;
  height: 100%;
  flex-shrink: 0;
`;

const Error = styled.div.attrs(({ _show }) => ({
  className: classNames(`${meta.prefix}-item-error`, {
    [`${meta.prefix}-item-errorShow`]: _show
  })
}))`
  opacity: ${({ _show }) => (_show ? 1 : 0)};
  position: absolute;
  width: 100%;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: rgb(255, 255, 255);
  background-color: rgba(17, 17, 17, 0.9);
  top: ${({ _show }) => (_show ? '0' : '-10px')};
  padding: 20px;
  transition: 0.3s ease all;
  box-sizing: border-box;
  z-index: 1;
`;
